import React, { useState, useEffect } from 'react';
import { useCMS } from '../../context/CMSContext';
import { OLTRAYA_COMPLETE_LOGO } from '../../data/defaultLogo';

interface SiteLoaderProps {
  isVisible?: boolean;
}

export const SiteLoader: React.FC<SiteLoaderProps> = ({ isVisible = true }) => {
  const { data } = useCMS();
  const settings = data?.siteSettings;
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (!isVisible && shouldRender) {
      setIsFading(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsFading(false);
      }, 700);
      return () => clearTimeout(timer);
    } else if (isVisible) {
      setShouldRender(true);
      setIsFading(false);
    }
  }, [isVisible, shouldRender]);

  if (!shouldRender) return null;

  // Directly show the complete logo from frame 0 - strictly no text fallback
  const logoSource = settings?.loaderLogoUrl || settings?.logoImageUrl || OLTRAYA_COMPLETE_LOGO;
  const baseLogoHeight = Math.round(Math.max(settings?.logoHeight || 66, 60) * 1.3); // ~86px
  const maxLogoWidth = Math.round(baseLogoHeight * (500 / 265)); // Proportional aspect ratio

  return (
    <div 
      className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-[#0d0f13] text-[#f5f2eb] select-none transition-all duration-700 ease-in-out ${
        isFading ? 'opacity-0 pointer-events-none scale-98' : 'opacity-100 scale-100'
      }`}
    >
      {/* Subtle Luxury Ambient Glow */}
      <div className="absolute w-[28rem] h-[28rem] rounded-full bg-[#a88345]/15 blur-3xl pointer-events-none animate-pulse" />

      <div className="relative z-10 flex flex-col items-center text-center space-y-8 max-w-lg px-6 animate-in fade-in zoom-in-95 duration-500">
        {/* Central Complete Logo (Directly shown, no text fallback) */}
        <div className="relative flex items-center justify-center p-4">
          {/* Soft Ambient Halo behind the logo */}
          <div className="absolute inset-0 bg-radial from-[#a88345]/30 to-transparent blur-2xl scale-125 pointer-events-none" />
          
          <img
            src={logoSource}
            alt="Oltraya Logo"
            width={maxLogoWidth}
            height={baseLogoHeight}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{
              maxHeight: `${baseLogoHeight}px`,
              maxWidth: `${maxLogoWidth}px`,
              objectFit: 'contain'
            }}
            className="relative z-10 transition-transform duration-700 hover:scale-105 filter drop-shadow-[0_6px_28px_rgba(0,0,0,0.75)]"
          />
        </div>

        {/* Minimal Architectural Shimmer Hairline */}
        <div className="flex flex-col items-center pt-2">
          <div className="w-44 h-[2.5px] bg-white/15 rounded-full overflow-hidden relative">
            <div
              className="absolute inset-y-0 w-20 bg-gradient-to-r from-transparent via-[#a88345] to-transparent"
              style={{
                animation: 'oltrayaShimmer 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite'
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes oltrayaShimmer {
          0% {
            transform: translateX(-100%);
            opacity: 0.2;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateX(250%);
            opacity: 0.2;
          }
        }
      `}</style>
    </div>
  );
};
